/**
 * The family-side language notices of the form renderer (#2109 R2 lane L3).
 *
 * - R2-B1: a form follows the UI language. When the version does not offer
 *   the reader’s language it renders in English, and ONE line in the reader’s
 *   language says so.
 * - R2-B18: when any displayed string is a machine translation, ONE
 *   form-level line says so. In the wizard it stands down while the shell's
 *   machine-translation banner shows, which says it already (#2431). It is not a per-string marker; the only
 *   per-string marker is the “shown in English” note on a stale fallback
 *   (`ShownInEnglish`, below).
 * - R2-B12: the consent sentences stay English and authoritative, with a
 *   one-line notice beside them.
 *
 * TRF ruled all three as built on 2026-10-16 (docs/DECISIONS.md), so these
 * lines carry no `(?)` marker.
 */
import { useTranslation } from 'react-i18next';
import { useMachineBannerVisible } from '@/components/layout/MachineTranslationBanner';
import type { FormLocalization } from './FormDefinitionGate';

const NOTE = 'text-body-sm leading-relaxed text-stevens-ink-3';

export function FormLanguageNotice({
  localization,
  className,
  deferToShellBanner = false,
}: {
  localization: FormLocalization;
  className?: string;
  /** #2431 (D-2405-2431): leave the machine line to the shell's
   *  machine-translation banner while it shows. The application and renewal
   *  wizard passes it; on a phone the two lines stacked with the terms banner
   *  pushed the first question below the fold. The line returns once the
   *  family dismisses the banner. */
  deferToShellBanner?: boolean;
}) {
  const { t } = useTranslation('forms');
  const bannerVisible = useMachineBannerVisible();
  const machine = localization.machine && !(deferToShellBanner && bannerVisible);
  if (!localization.notOffered && !machine) return null;
  return (
    <div className={className} data-testid="form-language-notice">
      {localization.notOffered && (
        <p role="note" className={NOTE}>
          {t('language.notOffered')}
        </p>
      )}
      {machine && (
        <p role="note" className={NOTE}>
          {t('language.machine')}
        </p>
      )}
    </div>
  );
}

/** R2-B12: beside a consent sentence that stays in English. */
export function ConsentEnglishNotice({ show, className }: { show: boolean; className?: string }) {
  const { t } = useTranslation('forms');
  if (!show) return null;
  return (
    <p role="note" className={className ?? NOTE}>
      {t('language.consentEnglish')}
    </p>
  );
}

/** The per-string note on a stale fallback (R2-B18): this one string is in
 *  English because its translation was made from older wording. */
export function ShownInEnglish() {
  const { t } = useTranslation('forms');
  return <span className="ms-1.5 text-caption font-normal text-stevens-ink-3">({t('language.shownInEnglish')})</span>;
}
